import type { XLMModel, XLMNode, XLMValue } from "../api/e2e";

export interface FormulaAutocompleteContext {
  kind: "term" | "period";
  start: number;
  end: number;
  query: string;
  identifier?: string;
}

export interface FormulaAutocompleteSuggestion {
  key: string;
  kind: "function" | "line-item" | "period";
  badge: string;
  label: string;
  detail: string;
  insertText: string;
  caretOffset?: number;
}

export interface FormulaAutocompletePeriod {
  cadence: "annual" | "quarterly" | "half-year";
  year: number;
  part?: number;
}

interface FormulaFunction {
  name: string;
  signature: string;
}

// This is the exact function vocabulary evaluated by
// src/project/fdr/driver/src/project/core/xlm/src/xlm/compiler.py::_call_func.
export const XLM_FORMULA_FUNCTIONS: readonly FormulaFunction[] = [
  { name: "SUM", signature: "SUM(number, …)" },
  { name: "IF", signature: "IF(condition, if_true, if_false)" },
  { name: "IFERROR", signature: "IFERROR(value, fallback)" },
  { name: "IFNA", signature: "IFNA(value, fallback)" },
  { name: "ABS", signature: "ABS(number)" },
  { name: "MAX", signature: "MAX(number, …)" },
  { name: "MIN", signature: "MIN(number, …)" },
  { name: "AVERAGE", signature: "AVERAGE(number, …)" },
  { name: "POWER", signature: "POWER(base, exponent)" },
  { name: "COUNT", signature: "COUNT(value, …)" },
  { name: "COUNTA", signature: "COUNTA(value, …)" },
  { name: "ROUND", signature: "ROUND(number, digits)" },
  { name: "ROUNDUP", signature: "ROUNDUP(number, digits)" },
  { name: "ROUNDDOWN", signature: "ROUNDDOWN(number, digits)" },
  { name: "TRUE", signature: "TRUE()" },
  { name: "FALSE", signature: "FALSE()" },
] as const;

function hasValues(values: XLMValue[] | null | undefined): boolean {
  return Boolean(values?.length);
}

function hasAnyValues(node: XLMNode): boolean {
  return (
    hasValues(node.yearly_values) ||
    hasValues(node.quarterly_values) ||
    hasValues(node.half_year_values)
  );
}

function modelLineItems(
  model: XLMModel,
): { identifier: string; name: string }[] {
  const result = new Map<string, { identifier: string; name: string }>();
  const authoredTypes = new Set(["truth", "computed", "total"]);
  for (const node of model.nodes) {
    const hasReportedTotals = Boolean(
      node.breakdown &&
      (hasValues(node.breakdown.yearly_reported_totals) ||
        hasValues(node.breakdown.quarterly_reported_totals) ||
        hasValues(node.breakdown.half_year_reported_totals)),
    );
    if (
      !["section_title", "separator"].includes(node.type) &&
      (hasAnyValues(node) ||
        hasReportedTotals ||
        (!node.breakdown && authoredTypes.has(node.type)))
    ) {
      result.set(node.identifier, {
        identifier: node.identifier,
        name: node.name,
      });
    }
    for (const item of node.breakdown?.items ?? []) {
      if (hasAnyValues(item) || authoredTypes.has(item.type)) {
        result.set(item.identifier, {
          identifier: item.identifier,
          name: item.name,
        });
      }
    }
  }
  return [...result.values()];
}

interface FormulaPeriodOption extends FormulaAutocompletePeriod {
  key: string;
  token: string;
  label: string;
  badge: string;
  cadenceLabel: string;
  endingDate: string | null;
}

function formulaPeriodOptions(model: XLMModel): FormulaPeriodOption[] {
  return [
    ...model.fiscal_years.map((period) => ({
      key: `annual:${period.year}`,
      token: String(period.year),
      label: `FY${String(period.year).slice(-2)}`,
      badge: "FY",
      cadenceLabel: "Annual",
      cadence: "annual" as const,
      year: period.year,
      endingDate: period.ending_date ?? null,
    })),
    ...model.quarters.map((period) => ({
      key: `quarterly:${period.year}:${period.quarter}`,
      token: `${period.year}Q${period.quarter}`,
      label: `Q${period.quarter} FY${String(period.year).slice(-2)}`,
      badge: "Q",
      cadenceLabel: "Quarterly",
      cadence: "quarterly" as const,
      year: period.year,
      part: period.quarter,
      endingDate: period.ending_date ?? null,
    })),
    ...model.half_years.map((period) => ({
      key: `half-year:${period.year}:${period.half}`,
      token: `${period.year}H${period.half}`,
      label: `H${period.half} FY${String(period.year).slice(-2)}`,
      badge: "H",
      cadenceLabel: "Half-year",
      cadence: "half-year" as const,
      year: period.year,
      part: period.half,
      endingDate: period.ending_date ?? null,
    })),
  ];
}

function formulaPeriodLabel(period: FormulaAutocompletePeriod): string {
  const year = String(period.year).slice(-2);
  if (period.cadence === "quarterly") return `Q${period.part} FY${year}`;
  if (period.cadence === "half-year") return `H${period.part} FY${year}`;
  return `FY${year}`;
}

function periodPosition(period: FormulaAutocompletePeriod): number {
  if (period.cadence === "quarterly") {
    return period.year * 4 + (period.part ?? 0);
  }
  if (period.cadence === "half-year") {
    return period.year * 4 + (period.part ?? 0) * 2;
  }
  return period.year * 4 + 4;
}

function sameFormulaPeriod(
  left: FormulaAutocompletePeriod,
  right: FormulaAutocompletePeriod,
): boolean {
  return (
    left.cadence === right.cadence &&
    left.year === right.year &&
    left.part === right.part
  );
}

function periodSortTier(
  period: FormulaAutocompletePeriod,
  currentPeriod: FormulaAutocompletePeriod,
): number {
  if (sameFormulaPeriod(period, currentPeriod)) return 0;
  if (period.year === currentPeriod.year) return 1;
  if (period.cadence === currentPeriod.cadence) return 2;
  return 3;
}

function periodCadenceOrder(
  cadence: FormulaAutocompletePeriod["cadence"],
): number {
  if (cadence === "quarterly") return 0;
  if (cadence === "half-year") return 1;
  return 2;
}

function periodSuggestions(
  model: XLMModel,
  context: FormulaAutocompleteContext,
  currentPeriod: FormulaAutocompletePeriod | undefined,
): FormulaAutocompleteSuggestion[] {
  if (
    !context.identifier ||
    !modelLineItems(model).some(
      (item) => item.identifier === context.identifier,
    )
  ) {
    return [];
  }
  const query = context.query.toLocaleLowerCase();
  const compactQuery = normalized(query);
  const samePeriodMatches =
    query === "" || "same".startsWith(query) || "current".startsWith(query);
  const samePeriod: FormulaAutocompleteSuggestion[] = samePeriodMatches
    ? [
        {
          key: "period:same",
          kind: "period",
          badge: "=",
          label: "Same period",
          detail: currentPeriod
            ? `Uses ${formulaPeriodLabel(currentPeriod)}`
            : "Uses the formula cell's period",
          insertText: "",
        },
      ]
    : [];
  const options = formulaPeriodOptions(model)
    .filter(
      (period) =>
        query === "" ||
        period.token.toLocaleLowerCase().includes(query) ||
        normalized(period.label).includes(compactQuery),
    )
    .sort((left, right) => {
      if (!currentPeriod) {
        return (
          left.year - right.year || periodPosition(left) - periodPosition(right)
        );
      }
      const leftTier = periodSortTier(left, currentPeriod);
      const rightTier = periodSortTier(right, currentPeriod);
      const leftCurrentCadence = left.cadence === currentPeriod.cadence ? 0 : 1;
      const rightCurrentCadence =
        right.cadence === currentPeriod.cadence ? 0 : 1;
      return (
        leftTier - rightTier ||
        leftCurrentCadence - rightCurrentCadence ||
        periodCadenceOrder(left.cadence) - periodCadenceOrder(right.cadence) ||
        Math.abs(periodPosition(left) - periodPosition(currentPeriod)) -
          Math.abs(periodPosition(right) - periodPosition(currentPeriod)) ||
        periodPosition(left) - periodPosition(right)
      );
    })
    .map<FormulaAutocompleteSuggestion>((period) => ({
      key: `period:${period.key}`,
      kind: "period",
      badge: period.badge,
      label: period.label,
      detail: period.endingDate
        ? `${period.cadenceLabel} · ${period.endingDate}`
        : period.cadenceLabel,
      insertText: `[${period.token}]`,
    }));
  return [...samePeriod, ...options];
}

function insideFormulaString(value: string): boolean {
  let quoted = false;
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] !== '"') continue;
    if (quoted && value[index + 1] === '"') {
      index += 1;
      continue;
    }
    quoted = !quoted;
  }
  return quoted;
}

export function formulaAutocompleteContext(
  formula: string,
  caret: number,
): FormulaAutocompleteContext | null {
  if (!formula.startsWith("=")) return null;
  const end = Math.max(0, Math.min(caret, formula.length));
  const beforeCaret = formula.slice(0, end);
  if (insideFormulaString(beforeCaret)) return null;
  const periodMatch =
    /(?:^|[=+\-*/,(<>&])\s*(?<identifier>[0-9]*[A-Za-z_][0-9A-Za-z_]*)\[(?<query>[0-9A-Za-z]*)$/u.exec(
      beforeCaret,
    );
  if (periodMatch?.groups) {
    const query = periodMatch.groups.query ?? "";
    return {
      kind: "period",
      start: end - query.length - 1,
      end,
      query,
      identifier: periodMatch.groups.identifier,
    };
  }
  const match = /(?:^|[=+\-*/,(<>&])\s*(?<query>[0-9A-Za-z_]*)$/u.exec(
    beforeCaret,
  );
  const query = match?.groups?.query;
  if (query === undefined) return null;
  if (query === "" && beforeCaret.trim() !== "=") return null;
  return { kind: "term", start: end - query.length, end, query };
}

function normalized(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9]+/gu, "");
}

function searchTokens(value: string): string[] {
  return value
    .toLocaleLowerCase()
    .split(/[^a-z0-9]+/gu)
    .filter(Boolean);
}

export function formulaAutocompleteSuggestions(
  model: XLMModel,
  formula: string,
  caret: number,
  currentPeriod?: FormulaAutocompletePeriod,
  limit = 8,
): FormulaAutocompleteSuggestion[] {
  const context = formulaAutocompleteContext(formula, caret);
  if (!context) return [];
  if (context.kind === "period") {
    return periodSuggestions(model, context, currentPeriod);
  }
  const query = context.query.toLocaleLowerCase();
  const compactQuery = normalized(query);
  const functions = XLM_FORMULA_FUNCTIONS.filter((item) =>
    item.name.toLocaleLowerCase().startsWith(query),
  ).map<FormulaAutocompleteSuggestion>((item) => {
    const zeroArgument = item.name === "TRUE" || item.name === "FALSE";
    return {
      key: `function:${item.name}`,
      kind: "function",
      badge: "fx",
      label: item.name,
      detail: item.signature,
      insertText: zeroArgument ? `${item.name}()` : `${item.name}(`,
      ...(zeroArgument ? { caretOffset: item.name.length + 2 } : {}),
    };
  });
  if (query === "") return functions.slice(0, limit);

  const lineItems = modelLineItems(model)
    .flatMap((item) => {
      const identifier = item.identifier.toLocaleLowerCase();
      const compactName = normalized(item.name);
      const identifierPrefix = identifier.startsWith(query);
      const identifierTokenPrefix = searchTokens(identifier).some((token) =>
        token.startsWith(query),
      );
      const namePrefix = compactName.startsWith(compactQuery);
      const nameTokenPrefix = searchTokens(item.name).some((token) =>
        token.startsWith(compactQuery),
      );
      const containsMatch =
        compactQuery.length >= 3 && compactName.includes(compactQuery);
      if (
        !identifierPrefix &&
        !identifierTokenPrefix &&
        !namePrefix &&
        !nameTokenPrefix &&
        !containsMatch
      ) {
        return [];
      }
      return [
        {
          suggestion: {
            key: `line-item:${item.identifier}`,
            kind: "line-item" as const,
            badge: "Row",
            label: item.name,
            detail: item.identifier,
            insertText: `${item.identifier}[`,
          },
          rank: identifierPrefix
            ? 0
            : identifierTokenPrefix
              ? 1
              : namePrefix
                ? 2
                : nameTokenPrefix
                  ? 3
                  : 4,
        },
      ];
    })
    .sort(
      (left, right) =>
        left.rank - right.rank ||
        left.suggestion.label.localeCompare(right.suggestion.label),
    )
    .map((item) => item.suggestion);

  return [...functions, ...lineItems].slice(0, limit);
}

export function applyFormulaAutocomplete(
  formula: string,
  context: FormulaAutocompleteContext,
  suggestion: FormulaAutocompleteSuggestion,
): { formula: string; caret: number } {
  const next =
    formula.slice(0, context.start) +
    suggestion.insertText +
    formula.slice(context.end);
  return {
    formula: next,
    caret:
      context.start + (suggestion.caretOffset ?? suggestion.insertText.length),
  };
}
